/**
 * RecoveryModeDashboard.js
 * 
 * Platform owner dashboard for managing GPS recovery mode on vehicles.
 * Shows vehicles in recovery mode and overdue bookings with trackers.
 */
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { 
  Satellite, 
  Shield, 
  AlertTriangle, 
  MapPin, 
  Clock, 
  RefreshCw,
  ChevronRight,
  ChevronLeft,
  Car,
  User,
  Calendar,
  Phone,
  Eye,
  EyeOff,
  Lock,
  Unlock,
  FileText,
  AlertCircle,
  CheckCircle
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';

const RecoveryModeDashboard = () => {
  const { vehicleId } = useParams();
  const navigate = useNavigate();
  const { api, isPlatformOwner } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [vehicles, setVehicles] = useState([]);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [vehicleDetails, setVehicleDetails] = useState(null);
  const [accessLogs, setAccessLogs] = useState([]);
  
  // Dialogs
  const [showActivateDialog, setShowActivateDialog] = useState(false);
  const [showDeactivateDialog, setShowDeactivateDialog] = useState(false);
  const [activating, setActivating] = useState(false);
  
  // Activate form
  const [activateForm, setActivateForm] = useState({
    reason: 'booking_overdue',
    booking_id: '',
    contact_authorities: false,
    notes: ''
  });

  // Fetch all recovery vehicles
  const fetchRecoveryVehicles = async () => {
    try {
      setLoading(true);
      const res = await api.get('/platform-owner/recovery/vehicles');
      setVehicles(res.data.vehicles || []);
    } catch (err) {
      console.error('Failed to fetch recovery vehicles:', err);
      toast.error('Failed to load recovery data');
    } finally {
      setLoading(false);
    }
  };

  // Fetch specific vehicle details
  const fetchVehicleDetails = async (vId) => {
    try {
      const res = await api.get(`/tracker/vehicles/${vId}/recovery-details`);
      setVehicleDetails(res.data);
      
      // Also fetch access logs
      try {
        const logsRes = await api.get(`/tracker/vehicles/${vId}/access-logs?limit=20`);
        setAccessLogs(logsRes.data.logs || []);
      } catch {
        setAccessLogs([]);
      }
    } catch (err) {
      console.error('Failed to fetch vehicle details:', err);
      toast.error('Failed to load vehicle details');
    }
  };

  useEffect(() => {
    if (!isPlatformOwner()) {
      navigate('/');
      return;
    }
    fetchRecoveryVehicles();
  }, []);

  useEffect(() => {
    if (vehicleId) {
      setSelectedVehicle(vehicleId);
      fetchVehicleDetails(vehicleId);
    }
  }, [vehicleId]);

  // Activate recovery mode
  const handleActivateRecovery = async () => {
    if (!selectedVehicle) return;
    
    setActivating(true);
    try {
      await api.post(`/tracker/vehicles/${selectedVehicle}/recovery-mode`, {
        reason: activateForm.reason,
        booking_id: activateForm.booking_id || null,
        contact_authorities: activateForm.contact_authorities,
        notes: activateForm.notes
      });
      toast.success('Recovery mode activated');
      setShowActivateDialog(false);
      fetchRecoveryVehicles();
      fetchVehicleDetails(selectedVehicle);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to activate recovery mode');
    } finally {
      setActivating(false);
    }
  };

  // Deactivate recovery mode
  const handleDeactivateRecovery = async () => {
    if (!selectedVehicle) return;
    
    setActivating(true);
    try {
      await api.post(`/tracker/vehicles/${selectedVehicle}/recovery-mode/deactivate`);
      toast.success('Recovery mode deactivated');
      setShowDeactivateDialog(false);
      fetchRecoveryVehicles();
      fetchVehicleDetails(selectedVehicle);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to deactivate recovery mode');
    } finally {
      setActivating(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'online': return <Badge className="bg-green-100 text-green-800">Online</Badge>;
      case 'offline': return <Badge className="bg-red-100 text-red-800">Offline</Badge>;
      default: return <Badge className="bg-slate-100 text-slate-800">Unknown</Badge>;
    }
  };

  const getReasonLabel = (reason) => {
    switch (reason) {
      case 'booking_overdue': return 'Overdue Booking';
      case 'dispute': return 'Active Dispute';
      case 'theft_suspected': return 'Suspected Theft';
      case 'admin_action': return 'Admin Action';
      default: return reason || 'Unknown';
    }
  };

  // List view
  if (!vehicleId) {
    return (
      <div className="min-h-screen bg-slate-50 p-6">
        <div className="max-w-6xl mx-auto space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
                <Shield className="w-7 h-7 text-red-600" />
                Recovery Mode Management
              </h1>
              <p className="text-slate-600 mt-1">
                Manage vehicle tracking for security and recovery situations
              </p>
            </div>
            <Button variant="outline" onClick={fetchRecoveryVehicles} disabled={loading}>
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>

          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card className="border-red-200 bg-red-50">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center">
                    <Shield className="w-5 h-5 text-red-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-red-700">
                      {vehicles.filter(v => v.recovery_mode.active).length}
                    </p>
                    <p className="text-sm text-red-600">In Recovery Mode</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="border-amber-200 bg-amber-50">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                    <AlertTriangle className="w-5 h-5 text-amber-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-amber-700">
                      {vehicles.filter(v => !v.recovery_mode.active && v.booking?.days_overdue > 0).length}
                    </p>
                    <p className="text-sm text-amber-600">Overdue (Tracked)</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="border-slate-200">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center">
                    <Satellite className="w-5 h-5 text-slate-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-700">{vehicles.length}</p>
                    <p className="text-sm text-slate-600">Total Monitored</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Vehicles List */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Vehicles Requiring Attention</CardTitle>
              <CardDescription>
                Click on a vehicle to view details and manage recovery mode
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <RefreshCw className="w-6 h-6 animate-spin text-slate-400" />
                </div>
              ) : vehicles.length === 0 ? (
                <div className="text-center py-12">
                  <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
                  <p className="text-slate-600">No vehicles in recovery mode or overdue</p>
                  <p className="text-sm text-slate-500 mt-1">All tracked vehicles are operating normally</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {vehicles.map((vehicle) => (
                    <div
                      key={vehicle.vehicle_id}
                      className={`p-4 rounded-lg border cursor-pointer transition-colors ${
                        vehicle.recovery_mode.active
                          ? 'bg-red-50 border-red-300 hover:bg-red-100'
                          : 'bg-amber-50 border-amber-200 hover:bg-amber-100'
                      }`}
                      onClick={() => navigate(`/platform-owner/recovery/${vehicle.vehicle_id}`)}
                      data-testid={`recovery-vehicle-${vehicle.vehicle_id}`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-4">
                          <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                            vehicle.recovery_mode.active ? 'bg-red-200' : 'bg-amber-200'
                          }`}>
                            <Car className={`w-6 h-6 ${
                              vehicle.recovery_mode.active ? 'text-red-700' : 'text-amber-700'
                            }`} />
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="font-semibold text-slate-900">{vehicle.vehicle_name}</span>
                              {vehicle.recovery_mode.active && (
                                <Badge className="bg-red-600 text-white animate-pulse">
                                  RECOVERY MODE
                                </Badge>
                              )}
                              {getStatusBadge(vehicle.tracker_status)}
                            </div>
                            <p className="text-sm text-slate-600 mt-1">
                              Owner: {vehicle.owner?.name || 'Unknown'}
                            </p>
                            {vehicle.booking && (
                              <p className="text-sm text-slate-600">
                                Booking: {vehicle.booking.booking_id}
                                {vehicle.booking.days_overdue > 0 && (
                                  <span className="text-red-600 font-medium ml-2">
                                    ({vehicle.booking.days_overdue} day{vehicle.booking.days_overdue !== 1 ? 's' : ''} overdue)
                                  </span>
                                )}
                              </p>
                            )}
                            {vehicle.recovery_mode.active && (
                              <p className="text-sm text-red-600 mt-1">
                                Reason: {getReasonLabel(vehicle.recovery_mode.reason)}
                              </p>
                            )}
                          </div>
                        </div>
                        
                        <Button variant="outline" size="sm">
                          View Details
                          <ChevronRight className="w-4 h-4 ml-1" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Detail view for a specific vehicle
  return (
    <div className="min-h-screen bg-slate-50 p-6">
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Back button */}
        <Button variant="ghost" onClick={() => navigate('/platform-owner/recovery')}>
          <ChevronLeft className="w-4 h-4 mr-1" />
          Back to Recovery Dashboard
        </Button>

        {/* Vehicle Header */}
        {vehicleDetails && (
          <>
            <Card className={vehicleDetails.access_state?.recovery_mode_active ? 'border-red-300 bg-red-50/30' : ''}>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-xl flex items-center gap-2">
                      <Car className="w-6 h-6 text-slate-600" />
                      {vehicleDetails.vehicle_name}
                      {vehicleDetails.access_state?.recovery_mode_active && (
                        <Badge className="bg-red-600 text-white animate-pulse">
                          RECOVERY MODE ACTIVE
                        </Badge>
                      )}
                    </CardTitle>
                    <CardDescription className="mt-1">
                      Vehicle ID: {vehicleDetails.vehicle_id}
                    </CardDescription>
                  </div>
                  <div className="flex items-center gap-2">
                    {getStatusBadge(vehicleDetails.tracker_status)}
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => fetchVehicleDetails(vehicleId)}
                    >
                      <RefreshCw className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              
              <CardContent className="space-y-6">
                {/* Access State */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="p-4 bg-slate-100 rounded-lg">
                    <h3 className="font-medium text-slate-700 mb-3 flex items-center gap-2">
                      <Lock className="w-4 h-4" />
                      Access Control Status
                    </h3>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-slate-600">Recovery Mode</span>
                        <Badge className={vehicleDetails.access_state?.recovery_mode_active ? 'bg-red-100 text-red-800' : 'bg-slate-100 text-slate-600'}>
                          {vehicleDetails.access_state?.recovery_mode_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-slate-600">Booking Overdue</span>
                        <Badge className={vehicleDetails.access_state?.booking_overdue ? 'bg-amber-100 text-amber-800' : 'bg-slate-100 text-slate-600'}>
                          {vehicleDetails.access_state?.booking_overdue ? 'Yes' : 'No'}
                        </Badge>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-slate-600">Dispute Active</span>
                        <Badge className={vehicleDetails.access_state?.dispute_active ? 'bg-purple-100 text-purple-800' : 'bg-slate-100 text-slate-600'}>
                          {vehicleDetails.access_state?.dispute_active ? 'Yes' : 'No'}
                        </Badge>
                      </div>
                      <div className="flex items-center justify-between pt-2 border-t">
                        <span className="text-sm font-medium text-slate-700">Tracking Unlocked</span>
                        <Badge className={vehicleDetails.access_state?.tracking_unlocked ? 'bg-green-100 text-green-800' : 'bg-slate-100 text-slate-600'}>
                          {vehicleDetails.access_state?.tracking_unlocked ? (
                            <><Unlock className="w-3 h-3 mr-1" /> Unlocked</>
                          ) : (
                            <><Lock className="w-3 h-3 mr-1" /> Restricted</>
                          )}
                        </Badge>
                      </div>
                    </div>
                  </div>

                  {/* Tracker Info */}
                  <div className="p-4 bg-slate-100 rounded-lg">
                    <h3 className="font-medium text-slate-700 mb-3 flex items-center gap-2">
                      <Satellite className="w-4 h-4" />
                      Tracker Information
                    </h3>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-slate-600">Provider</span>
                        <span className="text-sm font-medium capitalize">{vehicleDetails.tracker_provider}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-slate-600">Status</span>
                        {getStatusBadge(vehicleDetails.tracker_status)}
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-slate-600">Last Seen</span>
                        <span className="text-sm">
                          {vehicleDetails.last_seen_at 
                            ? format(new Date(vehicleDetails.last_seen_at), 'MMM d, HH:mm')
                            : 'Never'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Location Data (if unlocked) */}
                {vehicleDetails.location?.available && (
                  <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <h3 className="font-medium text-blue-800 mb-3 flex items-center gap-2">
                      <MapPin className="w-4 h-4" />
                      Last Known Location
                      <Badge className="bg-blue-100 text-blue-700 text-xs">
                        Access: {vehicleDetails.location.access_reason}
                      </Badge>
                    </h3>
                    <div className="text-sm text-blue-700">
                      {vehicleDetails.location.data?.address || (
                        vehicleDetails.location.data?.lat && vehicleDetails.location.data?.lng
                          ? `${vehicleDetails.location.data.lat.toFixed(5)}, ${vehicleDetails.location.data.lng.toFixed(5)}`
                          : 'Location data not available'
                      )}
                    </div>
                    {vehicleDetails.location.updated_at && (
                      <p className="text-xs text-blue-600 mt-2">
                        Updated: {format(new Date(vehicleDetails.location.updated_at), 'MMM d, yyyy HH:mm')}
                      </p>
                    )}
                  </div>
                )}

                {/* Location Restricted Notice */}
                {!vehicleDetails.location?.available && (
                  <div className="p-4 bg-slate-100 border border-slate-200 rounded-lg">
                    <div className="flex items-start gap-3">
                      <EyeOff className="w-5 h-5 text-slate-500 mt-0.5" />
                      <div>
                        <h3 className="font-medium text-slate-700">Location Access Restricted</h3>
                        <p className="text-sm text-slate-600 mt-1">
                          {vehicleDetails.location?.message || 'Location tracking is restricted during normal operation to protect renter privacy.'}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Recovery Mode Details */}
                {vehicleDetails.recovery_mode?.active && (
                  <div className="p-4 bg-red-100 border border-red-300 rounded-lg">
                    <h3 className="font-medium text-red-800 mb-3 flex items-center gap-2">
                      <Shield className="w-4 h-4" />
                      Recovery Mode Details
                    </h3>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-red-600">Activated:</span>
                        <span className="ml-2 text-red-800">
                          {vehicleDetails.recovery_mode.activated_at
                            ? format(new Date(vehicleDetails.recovery_mode.activated_at), 'MMM d, yyyy HH:mm')
                            : 'Unknown'}
                        </span>
                      </div>
                      <div>
                        <span className="text-red-600">Reason:</span>
                        <span className="ml-2 text-red-800">
                          {getReasonLabel(vehicleDetails.recovery_mode.reason)}
                        </span>
                      </div>
                      {vehicleDetails.recovery_mode.authorities_contacted && (
                        <div className="col-span-2">
                          <Badge className="bg-red-200 text-red-800">
                            Authorities Contacted
                          </Badge>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Current Booking Info */}
                {vehicleDetails.current_booking && (
                  <div className={`p-4 rounded-lg border ${
                    vehicleDetails.current_booking.is_overdue 
                      ? 'bg-amber-50 border-amber-300' 
                      : 'bg-slate-50 border-slate-200'
                  }`}>
                    <h3 className="font-medium text-slate-700 mb-3 flex items-center gap-2">
                      <Calendar className="w-4 h-4" />
                      Current Booking
                      {vehicleDetails.current_booking.is_overdue && (
                        <Badge className="bg-amber-600 text-white">
                          {vehicleDetails.current_booking.days_overdue} day{vehicleDetails.current_booking.days_overdue !== 1 ? 's' : ''} overdue
                        </Badge>
                      )}
                    </h3>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-slate-600">Booking ID:</span>
                        <Link 
                          to={`/bookings/${vehicleDetails.current_booking.booking_id}`}
                          className="ml-2 text-blue-600 hover:underline"
                        >
                          {vehicleDetails.current_booking.booking_id}
                        </Link>
                      </div>
                      <div>
                        <span className="text-slate-600">Status:</span>
                        <span className="ml-2 capitalize">{vehicleDetails.current_booking.status}</span>
                      </div>
                      <div>
                        <span className="text-slate-600">End Date:</span>
                        <span className="ml-2">{vehicleDetails.current_booking.end_date}</span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex gap-3 pt-4 border-t">
                  {!vehicleDetails.access_state?.recovery_mode_active ? (
                    <Button 
                      className="bg-red-600 hover:bg-red-700"
                      onClick={() => {
                        // Pre-fill form if booking exists
                        if (vehicleDetails.current_booking) {
                          setActivateForm(prev => ({
                            ...prev,
                            booking_id: vehicleDetails.current_booking.booking_id,
                            reason: vehicleDetails.current_booking.is_overdue ? 'booking_overdue' : 'admin_action'
                          }));
                        }
                        setShowActivateDialog(true);
                      }}
                      data-testid="activate-recovery-btn"
                    >
                      <Shield className="w-4 h-4 mr-2" />
                      Activate Recovery Mode
                    </Button>
                  ) : (
                    <Button 
                      variant="outline"
                      className="border-green-600 text-green-700 hover:bg-green-50"
                      onClick={() => setShowDeactivateDialog(true)}
                      data-testid="deactivate-recovery-btn"
                    >
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Deactivate Recovery Mode
                    </Button>
                  )}
                  
                  <Link to={`/bookings/${vehicleDetails.current_booking?.booking_id}`}>
                    <Button variant="outline" disabled={!vehicleDetails.current_booking}>
                      <FileText className="w-4 h-4 mr-2" />
                      View Booking
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>

            {/* Access Logs */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Eye className="w-5 h-5 text-slate-600" />
                  Tracker Access Audit Log
                </CardTitle>
                <CardDescription>
                  Record of all location data accesses for this vehicle
                </CardDescription>
              </CardHeader>
              <CardContent>
                {accessLogs.length === 0 ? (
                  <p className="text-sm text-slate-500 text-center py-4">No access logs recorded</p>
                ) : (
                  <div className="space-y-2">
                    {accessLogs.map((log, idx) => (
                      <div key={idx} className="p-3 bg-slate-50 rounded-lg text-sm">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Badge className="bg-slate-200 text-slate-700 text-xs">
                              {log.access_reason || log.access_type}
                            </Badge>
                            <span className="text-slate-600">by {log.accessed_by}</span>
                          </div>
                          <span className="text-xs text-slate-500">
                            {format(new Date(log.accessed_at), 'MMM d, HH:mm')}
                          </span>
                        </div>
                        {log.notes && (
                          <p className="text-xs text-slate-500 mt-1">{log.notes}</p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        )}

        {/* Activate Recovery Mode Dialog */}
        <Dialog open={showActivateDialog} onOpenChange={setShowActivateDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-700">
                <Shield className="w-5 h-5" />
                Activate Recovery Mode
              </DialogTitle>
              <DialogDescription>
                This will enable full location tracking for this vehicle. All access will be logged.
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div>
                <Label>Reason</Label>
                <select
                  className="w-full mt-1 p-2 border rounded-md"
                  value={activateForm.reason}
                  onChange={(e) => setActivateForm(prev => ({ ...prev, reason: e.target.value }))}
                >
                  <option value="booking_overdue">Overdue Booking</option>
                  <option value="dispute">Active Dispute</option>
                  <option value="theft_suspected">Suspected Theft</option>
                  <option value="admin_action">Administrative Action</option>
                </select>
              </div>
              
              <div>
                <Label>Booking ID (optional)</Label>
                <Input
                  value={activateForm.booking_id}
                  onChange={(e) => setActivateForm(prev => ({ ...prev, booking_id: e.target.value }))}
                  placeholder="e.g., book_abc123"
                />
              </div>
              
              <div className="flex items-center gap-2">
                <Switch
                  checked={activateForm.contact_authorities}
                  onCheckedChange={(checked) => setActivateForm(prev => ({ ...prev, contact_authorities: checked }))}
                />
                <Label>Authorities have been contacted</Label>
              </div>
              
              <div>
                <Label>Notes</Label>
                <Textarea
                  value={activateForm.notes}
                  onChange={(e) => setActivateForm(prev => ({ ...prev, notes: e.target.value }))}
                  placeholder="Additional context..."
                  rows={3}
                />
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowActivateDialog(false)}>
                Cancel
              </Button>
              <Button 
                className="bg-red-600 hover:bg-red-700"
                onClick={handleActivateRecovery}
                disabled={activating}
              >
                {activating ? 'Activating...' : 'Activate Recovery Mode'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Deactivate Recovery Mode Dialog */}
        <Dialog open={showDeactivateDialog} onOpenChange={setShowDeactivateDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-green-700">
                <CheckCircle className="w-5 h-5" />
                Deactivate Recovery Mode
              </DialogTitle>
              <DialogDescription>
                This will disable enhanced location tracking and return to normal access restrictions.
              </DialogDescription>
            </DialogHeader>
            
            <div className="py-4">
              <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-sm text-green-700">
                  Location tracking will be restricted again to protect renter privacy during normal operation.
                </p>
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDeactivateDialog(false)}>
                Cancel
              </Button>
              <Button 
                className="bg-green-600 hover:bg-green-700"
                onClick={handleDeactivateRecovery}
                disabled={activating}
              >
                {activating ? 'Deactivating...' : 'Deactivate Recovery Mode'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default RecoveryModeDashboard;
